import React, {useState} from 'react';
import {Modal} from "../../../utils/modal/Modal";
import {materialError} from "./materialError";
import {useDispatch} from "react-redux";
import {toast} from "react-toastify";
import {materialStageSaveAction} from "../../../actions/materialAction";
import {InputProfile} from "../../../utils/input/InputProfile";

/**
 * Заведение и правка ступени маршрута.
 *
 * Ступень — отрезок программы в днях от старта, полуинтервал: день начала входит,
 * день окончания уже принадлежит следующей. Границы задаются руками, потому что
 * они должны совпадать с периодами курса в CRM — по ним же сервер решает, на
 * какой ступени клиентка сейчас.
 *
 * Дни, а не недели: неделя — это то, как о сроке говорят с клиенткой, а считает
 * сервер днями, и вводить надо то, что он считает.
 *
 * Ступени курса система уже знает: выбор из них заполняет форму сам. Набирать
 * руками то, что известно, значит однажды ошибиться в дне — и разойтись с
 * расчётом ступени на сервере.
 */
export function StageModal({item, presets, trigger, triggerWrap, onSuccess}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(item ? item.title : '');
  const [uname, setUname] = useState(item && item.uname ? item.uname : '');
  const [position, setPosition] = useState(item ? String(item.order) : '');
  const [dayFrom, setDayFrom] = useState(item ? String(item.day_from ?? '') : '');
  const [dayTo, setDayTo] = useState(item ? String(item.day_to ?? '') : '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const handleOpen = (value) => {
    if (loading) {
      return;
    }
    if (value) {
      setTitle(item ? item.title : '');
      setUname(item && item.uname ? item.uname : '');
      setPosition(item ? String(item.order) : '');
      setDayFrom(item ? String(item.day_from ?? '') : '');
      setDayTo(item ? String(item.day_to ?? '') : '');
      setError('');
    }
    setOpen(value);
  };

  // Выбор известной ступени заполняет форму: править потом никто не мешает
  const handlePreset = (title) => {
    const preset = (presets || []).find((p) => p.title === title);
    if (!preset) {
      return;
    }
    setTitle(preset.title);
    setPosition(String(preset.order));
    setDayFrom(String(preset.day_from));
    setDayTo(String(preset.day_to));
    setError('');
  };

  const handleSubmit = () => {
    if (loading) {
      return false;
    }
    if (uname.trim() !== '' && !/^[a-zA-Z][a-zA-Z0-9_]*$/.test(uname.trim())) {
      setError('uname');
      toast.error('Имя ступени — латиница, цифры и подчёркивание, начиная с буквы',
        {position: toast.POSITION.BOTTOM_RIGHT});
      return false;
    }
    if (title.trim() === '') {
      setError('title');
      return false;
    }
    // Пустой отрезок — ступень, на которой не бывает ни одна клиентка: маршрут
    // молча получил бы дыру, и заметили бы её только по жалобе
    if (Number(dayTo) <= Number(dayFrom)) {
      setError('days');
      return false;
    }
    setError('');
    setLoading(true);

    dispatch(materialStageSaveAction({
      id: item ? item.id : '',
      title: title.trim(),
      uname: uname.trim(),
      order: Number(position) || 0,
      day_from: Number(dayFrom) || 0,
      day_to: Number(dayTo) || 0,
    })).then(() => {
      setLoading(false);
      setOpen(false);
      onSuccess();
    }).catch((e) => {
      setLoading(false);
      toast.error(materialError(e), {position: toast.POSITION.BOTTOM_RIGHT});
    });
  };

  return <Modal isStatic={true} open={open} handleOpen={handleOpen} trigger={trigger} triggerWrap={triggerWrap}>
    <React.Fragment>
      <div className="modal__title">{item ? "Правка" : "Новая"} ступень{item ? "и" : ""}</div>
      <div className="modal__wrap">
        {presets && presets.length > 0 && <div className="profile__group">
          <p className="profile__help help">Ступень курса</p>
          <select className="profile__input input" value="" disabled={loading}
                  onChange={(e) => handlePreset(e.target.value)}>
            <option value="">Выберите, чтобы заполнить поля</option>
            {presets.map((preset) => <option key={preset.title} value={preset.title}>
              {preset.order}. {preset.title} ({preset.day_from}–{preset.day_to} день){preset.exists ? ' — уже заведена' : ''}
            </option>)}
          </select>
        </div>}
        <InputProfile type="text" title="Название" name="title" placeholder="Например: Детокс"
                      value={title} error={error === 'title'} disabled={loading}
                      onChange={(e) => setTitle(e.target.value)}/>
        {/* Имя стоит внутри фигурных скобок в тексте сообщения, поэтому
            латиницей: «{Психологическая неделя_1}» куратор не наберёт */}
        <InputProfile type="text" title="Короткое имя (латиницей)" name="uname" placeholder="detox"
                      value={uname} error={error === 'uname'} disabled={loading}
                      onChange={(e) => setUname(e.target.value)}/>
        <p className="profile__help help">
          По нему собирается плейсхолдер материала для сообщения: {'{detox_1}'} — первый материал этой ступени.
          В приложении он превратится в карточку, в остальных каналах пропадёт.
        </p>
        <InputProfile type="number" title="Порядок в маршруте" name="order" placeholder="2"
                      value={position} disabled={loading}
                      onChange={(e) => setPosition(e.target.value)}/>
        <InputProfile type="number" title="День начала (от старта программы)" name="day_from" placeholder="7"
                      value={dayFrom} error={error === 'days'} disabled={loading}
                      onChange={(e) => setDayFrom(e.target.value)}/>
        <InputProfile type="number" title="День окончания (не входит в ступень)" name="day_to" placeholder="14"
                      value={dayTo} error={error === 'days'} disabled={loading}
                      onChange={(e) => setDayTo(e.target.value)}/>
        {error === 'days' && <p className="profile__help help">
          День окончания должен быть больше дня начала: иначе на ступени не окажется ни одной клиентки
        </p>}
      </div>
      <div className="modal__btns">
        <button type="button" className="modal__btn btn" onClick={handleSubmit} disabled={loading}>Сохранить</button>
      </div>
    </React.Fragment>
  </Modal>;
}
